import { BORDERLESS_BORDER } from '../borderStyles';
import { INFO_CARD_STYLE_SCHEMA } from './infoCardProps';
import { styleDefaults, styleKeys } from '../../theme/styleSchema';

/**
 * Styles de Carte info — un style n'est PAS une variante de rendu, c'est un
 * jeu de valeurs. Rien n'est persisté qui dirait « ceci est un témoignage » :
 * choisir un style en recopie les valeurs dans la carte, qui reste
 * « Personnalisé » (seul un style du thème se suit, cf. puck/themedBlocks).
 *
 * Même mécanique que les dispositions de section
 * (utils/proposalSectionPresets.js + puck/fields/SectionLayoutPresetPicker).
 */

/**
 * Les props qu'un style pose (cf. INFO_CARD_STYLE_SCHEMA). Le reste est du
 * CONTENU (value, description, badges, icon, imageUrl, logoUrl) : un
 * changement de style ne doit jamais y toucher, sinon basculer d'un style à
 * l'autre pour voir efface le travail de l'utilisateur.
 */
export const INFO_CARD_STYLE_PROPS = styleKeys(INFO_CARD_STYLE_SCHEMA);

/**
 * Ce qu'un style du THÈME du site pose : celles d'un style de la galerie, plus
 * les couleurs et l'ombre.
 */
export const INFO_CARD_THEME_STYLE_PROPS = styleKeys(INFO_CARD_STYLE_SCHEMA, ['gallery', 'theme']);

/** Base commune : chaque style ne réécrit que ce qui le distingue. */
const base = styleDefaults(INFO_CARD_STYLE_SCHEMA);

/**
 * Un modèle porte aussi un TEXTE D'EXEMPLE. Passer en « Témoignage » en
 * gardant « 98 % / Clients satisfaits » ne veut rien dire : le contenu de
 * démonstration suit le modèle choisi. Le texte de l'utilisateur, lui, n'est
 * jamais remplacé (cf. isInfoCardSampleContent).
 */
const preset = (key, label, props, sample) => ({
  key, label, props: { ...base, ...props }, sample,
});

/**
 * L'ordre est celui de la galerie : d'abord les cartes de données, puis les
 * cartes de personne, puis les citations.
 */
export const INFO_CARD_STYLE_PRESETS = [
  preset('figure', 'Chiffre clé', {}, {
    value: '98 %', description: 'Clients satisfaits',
  }),
  preset('figure-bare', 'Chiffre nu', { border: BORDERLESS_BORDER }, {
    value: '24 h', description: 'Délai de réponse moyen',
  }),
  preset('icon-row', 'Icône + texte', {
    titleStyle: 'heading',
    illustrationShape: 'square',
    illustrationSize: '3rem',
    cardLayout: 'horizontal',
    contentAlign: 'left',
  }, {
    value: 'Suivi dédié', description: 'Un interlocuteur unique du devis à la livraison.',
  }),
  preset('person', "Membre d'équipe", {
    titleStyle: 'heading',
    illustration: 'image',
    illustrationSize: '5rem',
  }, {
    value: 'Camille Martin', description: 'Cheffe de projet',
  }),
  preset('quote-card', 'Témoignage — carte', {
    titleStyle: 'quote',
    illustration: 'image',
    contentAlign: 'left',
    backgroundColor: '#f8f9fa',
    quoteMark: true,
  }, {
    value: 'Une équipe réactive, des délais tenus.', description: 'Camille Martin, Directrice',
  }),
  preset('quote-xl', 'Citation XXL', {
    titleStyle: 'quote-xl',
    illustration: 'none',
    border: BORDERLESS_BORDER,
    quoteMark: true,
  }, {
    value: 'Le projet a été livré avant la date prévue.', description: 'Camille Martin, Directrice',
  }),
  preset('quote-band', 'Témoignage — bandeau', {
    titleStyle: 'quote',
    illustration: 'image',
    illustrationSize: '6rem',
    cardLayout: 'horizontal',
    contentAlign: 'left',
  }, {
    value: 'Un accompagnement de bout en bout.', description: 'Camille Martin, Directrice',
  }),
  preset('review', 'Avis noté', {
    titleStyle: 'quote',
    illustration: 'image',
    illustrationSize: '3rem',
    contentAlign: 'left',
    rating: 5,
  }, {
    value: 'Rien à redire, nous retravaillerons ensemble.', description: 'Camille Martin, Directrice',
  }),
  preset('client-logo', 'Référence client', {
    titleStyle: 'quote',
    illustration: 'none',
  }, {
    value: 'Client depuis 2019', description: 'Trois projets menés ensemble',
  }),
];

/**
 * Tous les textes d'exemple des modèles, sous leur forme nue. Sert à
 * reconnaître un contenu de démonstration : lui seul est remplacé quand on
 * change de modèle.
 */
const SAMPLE_TEXTS = new Set(
  INFO_CARD_STYLE_PRESETS.flatMap((p) => [p.sample?.value, p.sample?.description])
    .filter(Boolean),
);

/** Texte HTML dépouillé de ses balises, pour comparer à un exemple. */
function plainText(html) {
  return String(html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
}

/**
 * Ce texte est-il encore celui d'un modèle (ou vide) ? Seul un tel texte est
 * remplacé au changement de modèle — ce que l'utilisateur a écrit reste.
 */
export function isInfoCardSampleContent(html) {
  const text = plainText(html);
  return !text || SAMPLE_TEXTS.has(text);
}

/**
 * Le contenu à poser en changeant de modèle : celui du modèle si la carte ne
 * portait que des exemples (ou rien), rien sinon.
 *
 * Tout ou rien : dès qu'un des deux textes est de l'utilisateur, aucun n'est
 * touché. Au champ par champ, un titre écrit à la main se retrouvait flanqué
 * de la description d'exemple du nouveau modèle, et une description laissée
 * vide exprès se remplissait.
 *
 * @returns {{ value?: string, description?: string }} patch de contenu
 */
export function infoCardSampleContentPatch(def, current = {}) {
  if (!def?.sample) return {};
  if (!isInfoCardSampleContent(current.value) || !isInfoCardSampleContent(current.description)) {
    return {};
  }
  const patch = {};
  if (def.sample.value !== undefined) patch.value = def.sample.value;
  if (def.sample.description !== undefined) patch.description = def.sample.description;
  return patch;
}

/**
 * Les styles de la galerie sous la forme d'un modèle de box (id/nom/valeurs),
 * pour les proposer à côté des modèles du configurateur.
 */
export function infoCardPresetTemplates() {
  return INFO_CARD_STYLE_PRESETS.map((p) => ({
    ...p.props, id: p.key, name: p.label, sample: p.sample,
  }));
}

export const INFO_CARD_DEFAULT_STYLE_KEY = 'figure';

/**
 * Le patch qu'un style écrit — rien d'autre. `border` est recopiée, chaque
 * carte doit avoir la sienne.
 */
export function infoCardStylePatch(def) {
  return { ...def.props, border: { ...def.props.border } };
}

/**
 * Les props d'une carte à enregistrer comme modèle dans la banque de la
 * société : tout son habillage (icône, badges, couleurs, bordure, disposition),
 * sauf la photo — elle appartient à la carte, pas au modèle, exactement comme
 * dans le configurateur du template (cf. InfoCardFields, qui ne propose ce
 * champ que sur une box).
 *
 * Les deux textes n'en font pas partie : ils vivent à côté du sac, en Draft.js.
 */
export function infoCardBankProps(card) {
  const { imageUrl, ...props } = card || {};
  return props;
}

/**
 * Applique un style : seules les props de style sont écrites, le contenu est
 * conservé tel quel.
 */
export function applyInfoCardStyle(props, def) {
  return { ...(props || {}), ...infoCardStylePatch(def) };
}
